import { useEffect, useSyncExternalStore } from 'react'
export type SharingState = { enabled: boolean; targetBusinessId: string | null; revision: string | null; availableTargets: { businessId: string; name: string; product: string }[]; operational: boolean }
export type SharingRequest = <T>(path: string, body?: unknown) => Promise<T>
type Snapshot = { data: SharingState | null; error: string | null; loading: boolean }
const entries = new Map<string, ReturnType<typeof createEntry>>()
function createEntry(path: string, request: SharingRequest) {
  let state: Snapshot = { data: null, error: null, loading: true }, pending: Promise<void> | null = null, loadedAt = 0
  const listeners = new Set<() => void>()
  const update = (next: Snapshot) => { state = next; listeners.forEach(listener => listener()) }
  const refresh = (force = false) => {
    if (pending) return pending
    if (!force && loadedAt > Date.now() - 30000) return Promise.resolve()
    pending = request<SharingState>(path).then(data => { loadedAt = Date.now(); update({ data, error: null, loading: false }) })
      .catch(error => { loadedAt = Date.now(); update({ ...state, error: String(error.message ?? error), loading: false }) }).finally(() => { pending = null })
    return pending
  }
  const save = async (targetBusinessId: string | null, enabled: boolean) => {
    const data = await request<SharingState>(path, { targetBusinessId, enabled, expectedRevision: state.data?.revision ?? null })
    update({ data, error: null, loading: false }); loadedAt = Date.now(); window.dispatchEvent(new Event('app-sharing-settings-changed'))
  }
  return { read: () => state, subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener) } }, refresh, save }
}
export function useSharing(request: SharingRequest, appName: 'CRM' | 'Workflow', businessKey: string, actorKey = '') {
  const key = `${actorKey}:${appName}:${businessKey}`
  if (!entries.has(key)) entries.set(key, createEntry(`/app-copy/${appName.toLowerCase()}/${businessKey}/sharing`, request))
  const entry = entries.get(key)!, snapshot = useSyncExternalStore(entry.subscribe, entry.read, entry.read)
  useEffect(() => {
    if (!businessKey) return
    void entry.refresh()
    const refresh = () => { void entry.refresh(true) }
    window.addEventListener('focus', refresh); window.addEventListener('app-sharing-settings-changed', refresh)
    return () => { window.removeEventListener('focus', refresh); window.removeEventListener('app-sharing-settings-changed', refresh) }
  }, [entry, businessKey])
  const data = businessKey ? snapshot.data : null
  return { ...snapshot, data, save: entry.save, refresh: entry.refresh,
    canCopy: !snapshot.error && !!data?.enabled && !!data.operational && !!data.availableTargets?.some(target => target.businessId === data.targetBusinessId) }
}
